import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowAction,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminRowActionsButton,
  AdminAlertText,
  AdminSupportingText,
  AdminSortableTableHeader,
  AdminIcon,
  AdminListFilterDropdown,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { getBusinessesForProfileScenarios } from '../../../data/profileModerationScenarios';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const ACCOUNT_STATUS_OPTIONS = ['Active', 'Inactive', 'Restricted', 'Banned', 'Closed'];
const VERIFICATION_STATUS_OPTIONS = ['Pending', 'Verified'];

const ACCOUNT_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...ACCOUNT_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];

const VERIFICATION_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...VERIFICATION_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];

/** Open flags = New or Active handling cases (Under Review / Open map to Active / New). */
const OPEN_FLAG_STATUSES = new Set(['new', 'active', 'open', 'under review', 'escalated', 'attached']);

function normalizeMobileDigits(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/\D/g, '');
}

function getOpenFlagsCount(business) {
  if (typeof business.openFlagsCount === 'number') return business.openFlagsCount;
  if (typeof business.overviewOpenFlagsCount === 'number') return business.overviewOpenFlagsCount;
  const list =
    business.moderationAndFlags && business.moderationAndFlags.length > 0
      ? business.moderationAndFlags
      : business.flags || [];
  if (!list.length) {
    const legacy = (business.moderationFlags || 0) + (business.fraudAlerts || 0);
    return legacy > 0 ? legacy : 0;
  }
  return list.filter((f) => {
    const st = String(f.status || '').toLowerCase().trim();
    return OPEN_FLAG_STATUSES.has(st) && st !== 'decided' && st !== 'closed' && st !== 'resolved';
  }).length;
}

/** Account Status: Active, Inactive, Restricted, Banned, Closed. */
function getAccountStatus(business) {
  const raw = String(business.status || business.accountStatus || '').trim();
  if (!raw) return 'Active';
  const lower = raw.toLowerCase();
  if (lower === 'active') return 'Active';
  if (lower === 'inactive' || lower === 'deleted') return 'Inactive';
  if (lower === 'restricted' || lower === 'under review' || lower === 'suspended') return 'Restricted';
  if (lower === 'banned' || lower === 'blocked') return 'Banned';
  if (lower === 'closed') return 'Closed';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Verification Status: Pending or Verified. */
function getVerificationStatus(business) {
  const raw = String(business.verificationStatus || '').trim();
  if (!raw) return 'Pending';
  const lower = raw.toLowerCase();
  if (lower === 'verified') return 'Verified';
  if (lower === 'pending') return 'Pending';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Trade Category — read-only primary category label. */
function getTradeCategory(business) {
  const raw = business?.category ?? business?.primaryTrade ?? business?.tradeCategory ?? '';
  const first = Array.isArray(raw) ? raw[0] : String(raw).split(',')[0];
  const s = String(first || '').trim();
  if (!s) return '—';
  const lower = s.toLowerCase();
  if (lower.includes('plumb')) return 'Plumbing';
  if (lower.includes('electric')) return 'Electrical';
  if (lower.includes('hvac')) return 'HVAC & Refrigeration';
  if (lower.includes('roof')) return 'Roofing';
  if (lower.includes('landscape')) return 'Landscaping';
  if (lower.includes('paint')) return 'Painting';
  if (lower.includes('clean')) return 'Cleaning';
  if (lower.includes('handyman')) return 'Handyman';
  if (lower.includes('carpent')) return 'Carpentry';
  return s.replace(/\s*\/\s*Gas\s*$/i, '').trim() || '—';
}

/** Location: Suburb, STATE postcode (e.g. Melbourne, VIC 3000). */
function getBusinessLocation(business) {
  const suburb = business.location?.suburb || business.suburb || '';
  const state = business.region || business.location?.state || business.state || '';
  const postcode = business.location?.postcode || business.postcode || '';
  if (suburb && state && postcode) return `${suburb}, ${state} ${postcode}`;
  if (suburb && postcode) return `${suburb} ${postcode}`;
  if (suburb && state) return `${suburb}, ${state}`;
  return suburb || state || postcode || '—';
}

function matchesSearchQuery(business, searchTerm) {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return true;
  }

  const term = searchTerm.toLowerCase().trim();
  const termDigits = normalizeMobileDigits(searchTerm);

  const businessNameMatch =
    business.businessName && business.businessName.toLowerCase().includes(term);

  const businessEmailMatch =
    (business.businessEmail && business.businessEmail.toLowerCase().includes(term)) ||
    (business.email && business.email.toLowerCase().includes(term));

  const businessIdMatch =
    (business.businessId && business.businessId.toLowerCase().includes(term)) ||
    (business.id && String(business.id).toLowerCase().includes(term));

  const mobileRaw =
    business.mobile ?? business.primaryContactNumber ?? business.phone ?? business.businessPhone ?? '';
  const mobileMatchText =
    mobileRaw &&
    String(mobileRaw).toLowerCase().replace(/\s/g, '').includes(term.replace(/\s/g, ''));
  const mobileMatchDigits =
    termDigits.length >= 3 && normalizeMobileDigits(mobileRaw).includes(termDigits);

  const abnDigits = normalizeMobileDigits(business.abn);
  const abnMatch =
    (business.abn && String(business.abn).toLowerCase().replace(/\s/g, '').includes(term.replace(/\s/g, ''))) ||
    (termDigits.length >= 3 && abnDigits.includes(termDigits));

  return (
    businessNameMatch ||
    businessEmailMatch ||
    businessIdMatch ||
    mobileMatchText ||
    mobileMatchDigits ||
    abnMatch
  );
}

const Businesses = () => {
  const navigate = useNavigate();

  // Business ID | Business | Trade Category | Location | Flags | Account Status | Member Since | Last Active | Action
  const gridTemplateColumns =
    'minmax(88px, 0.9fr) minmax(160px, 1.8fr) minmax(100px, 1fr) minmax(140px, 1.4fr) minmax(72px, 0.7fr) minmax(110px, 1.1fr) minmax(110px, 1fr) minmax(140px, 1.3fr) minmax(56px, auto)';

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    verificationStatus: null,
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);

  const statusRef = useRef(null);
  const verificationRef = useRef(null);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(verificationRef, () => setVerificationOpen(false));

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('lastActive');
  const [sortOrder, setSortOrder] = useState('desc');

  const [businessRowActionsOpen, setBusinessRowActionsOpen] = useState({});
  const businessRowActionsRefs = useRef({});

  const toggleBusinessRowActions = (rowKey) => {
    setBusinessRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(businessRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setBusinessRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchBusinesses = async () => {
      setLoading(true);
      setError(null);
      try {
        // TODO: Replace with actual API call
        await new Promise((resolve) => setTimeout(resolve, 500));
        const allBusinesses = getBusinessesForProfileScenarios();

        let filteredBusinesses = allBusinesses.filter((business) => {
          if (!matchesSearchQuery(business, debouncedSearch)) return false;

          const accountStatus = getAccountStatus(business);
          const matchesStatus =
            filters.status === null || accountStatus === filters.status;

          const verificationStatus = getVerificationStatus(business);
          const matchesVerification =
            filters.verificationStatus === null ||
            verificationStatus === filters.verificationStatus;

          return matchesStatus && matchesVerification;
        });

        filteredBusinesses.sort((a, b) => {
          let aValue;
          let bValue;

          if (sortField === 'openFlags') {
            aValue = getOpenFlagsCount(a);
            bValue = getOpenFlagsCount(b);
          } else if (sortField === 'businessName') {
            aValue = (a.businessName || '').toLowerCase();
            bValue = (b.businessName || '').toLowerCase();
          } else if (sortField === 'tradeCategory') {
            aValue = getTradeCategory(a).toLowerCase();
            bValue = getTradeCategory(b).toLowerCase();
          } else if (sortField === 'location') {
            aValue = getBusinessLocation(a).toLowerCase();
            bValue = getBusinessLocation(b).toLowerCase();
          } else if (sortField === 'status') {
            aValue = getAccountStatus(a);
            bValue = getAccountStatus(b);
          } else if (sortField === 'joined') {
            aValue = a.joined ? new Date(a.joined).getTime() : 0;
            bValue = b.joined ? new Date(b.joined).getTime() : 0;
          } else if (sortField === 'lastActive') {
            aValue = a.lastActive
              ? new Date(a.lastActive).getTime()
              : a.lastLogin
                ? new Date(a.lastLogin).getTime()
                : 0;
            bValue = b.lastActive
              ? new Date(b.lastActive).getTime()
              : b.lastLogin
                ? new Date(b.lastLogin).getTime()
                : 0;
          } else if (sortField === 'businessId') {
            aValue = String(a.businessId || a.id || '');
            bValue = String(b.businessId || b.id || '');
          } else {
            aValue = a[sortField];
            bValue = b[sortField];
          }

          if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        const totalCount = filteredBusinesses.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setBusinesses(filteredBusinesses.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
      } catch (err) {
        setError(err.message || 'Failed to load businesses');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'status') {
      setFilters((prev) => ({ ...prev, status: value }));
    } else if (filterName === 'verificationStatus') {
      setFilters((prev) => ({ ...prev, verificationStatus: value }));
    }
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return (
      date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ', ' +
      date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  const goToBusinessDetail = (business) => {
    const businessId = business.businessId || business.id;
    if (!businessId) return;
    navigate(`/admin/users/businesses/${businessId}`, { state: { business } });
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Businesses</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Business Name, Business ID, email, mobile, ABN"
              title="Search by Business Name, Business ID, Email, Mobile, or ABN"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search businesses"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Account Status"
                value={filters.status}
                options={ACCOUNT_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Verification Status"
                value={filters.verificationStatus}
                options={VERIFICATION_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('verificationStatus', value)}
                menuRef={verificationRef}
                open={verificationOpen}
                setOpen={setVerificationOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="text-alertred mb-4 block">{error}</AdminAlertText>
              <AdminRowAction onClick={() => window.location.reload()}>Retry</AdminRowAction>
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No businesses match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <>
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
                style={{ gridTemplateColumns }}
              >
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('businessId')}>
                    Business ID
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('businessName')}>
                    Business
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('tradeCategory')}>
                    Trade Category
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('location')}>
                    Location
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('openFlags')}>
                    Flags
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('status')}>
                    Account Status
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('joined')}>
                    Member Since
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('lastActive')}>
                    Last Active
                  </AdminSortableTableHeader>
                </div>
                <div className="flex items-center justify-center">
                  <AdminTableHeader>Action</AdminTableHeader>
                </div>
              </div>

              {businesses.map((business, index) => {
                const businessId = business.businessId || business.id;
                const rowKey = String(business.businessId || business.id || `row-${index}`);
                const openFlags = getOpenFlagsCount(business);
                const accountStatus = getAccountStatus(business);
                const locationLabel = getBusinessLocation(business);
                const tradeCategory = getTradeCategory(business);
                const nameDisplay = business.businessName || '—';

                return (
                  <div
                    key={business.id || index}
                    role="button"
                    tabIndex={0}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
                    style={{ gridTemplateColumns }}
                    onClick={() => goToBusinessDetail(business)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToBusinessDetail(business);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <AdminTableBody className="whitespace-nowrap">{businessId || '—'}</AdminTableBody>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <AdminRowAvatar
                        src={business.profilePicture || require('../../../assets/img/User.png')}
                        alt={nameDisplay}
                        onError={(e) => {
                          e.target.src = require('../../../assets/img/User.png');
                        }}
                      />
                      <AdminTableBody
                        emphasis
                        className="truncate"
                        title={nameDisplay}
                      >
                        {nameDisplay}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={tradeCategory}>
                        {tradeCategory}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={locationLabel}>
                        {locationLabel}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminTableBody className="tabular-nums">{openFlags}</AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminStatusBadge status={accountStatus} label={accountStatus} />
                    </div>

                    <div className="flex items-center">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatDateTime(business.joined)}
                      </AdminTableMeta>
                    </div>

                    <div className="flex items-center">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatDateTime(business.lastActive || business.lastLogin)}
                      </AdminTableMeta>
                    </div>

                    <div
                      className="flex items-center justify-center relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          businessRowActionsRefs.current[rowKey] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBusinessRowActions(rowKey);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminIcon>
                            <i className="fi fi-rs-menu-dots-vertical text-offblack" aria-hidden />
                          </AdminIcon>
                        </AdminRowActionsButton>
                        {businessRowActionsOpen[rowKey] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                goToBusinessDetail(business);
                                setBusinessRowActionsOpen((prev) => ({
                                  ...prev,
                                  [rowKey]: false,
                                }));
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Businesses;
